import {
  type ClipboardEvent,
  ComponentPropsWithoutRef,
  isValidElement,
  type RefObject,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MessageRole, STREAMING_CURSOR_CHAR, UUID_REGEX } from '@/constants';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import reactStringReplace from 'react-string-replace';
import { classNames } from 'primereact/utils';
import { ChatMessage as ChatMessageType, Citation, DraftInfo, MessageAttachedDocument } from '@/types/chat';
import type { FileStatus } from '@/types/file';
import { CitationButton, CitationPlaceholderPill } from '@/ui/features/chat/components/CitationButton';
import { useProjectChatContext } from '@/ui/features/chat/projectChat';
import { DraftArtefactCard } from './DraftArtefactCard';
import {
  CHAT_COPY_EXCLUDE_SELECTOR,
  cleanChatMessageForCopy,
  containsGfmTable,
  copySelectionWithoutCitations,
  isAiGeneratedCitation,
  isUuidLike,
  splitStreamingCursorContent,
} from '@/utils';
import {
  CheckIconV2,
  CopyIcon,
  DocxIcon,
  NextStepsIcon,
  PencilIcon,
  StarOutlinedIcon,
  Tooltip,
  TOOLTIP_SURFACE_BOTTOM,
} from '@repo/atomic-components';
import { usePostEventMessageCopiedMutation } from '@/api';
import { useEventCallback } from '@/hooks';
import { useFeedbackReminder } from '../hooks/useFeedbackReminder';
import { NextSteps } from './NextSteps';
import { KEEPS_CHAT_INPUT_OPEN_ATTR } from '../constants';
import { highlightMatches } from '@/utils/chatSearch';
import { MessageFeedbackButtons } from './MessageFeedbackButtons';
import { UploadFileButton } from './UploadFileButton';
import { ChatTableDialog } from './ChatTableDialog';
import { ExpandableTableCell } from './ExpandableTableCell';
import { MarkdownTable } from './MarkdownTable';
import { ChatResponseStamp } from './ChatResponseStamp';
import { copyToClipboard } from '@repo/utils';

export interface ChatMessageProps {
  message: ChatMessageType;
  onCitationClick: (citation: Citation) => void;
  /** When set, matching text is highlighted in the message */
  searchQuery?: string;
  /** Global ordered UUIDs across all messages for consistent citation numbering */
  globalOrderedUuids?: string[];
  chatCitations: Record<string, Citation>;
  documentsByMessageId?: Map<string, MessageAttachedDocument[]>;
  /** Live per-document status for the currently pending prompt attachments */
  promptAttachmentStatusById?: Map<string, FileStatus>;
  /** True when we are deferring send while documents are still processing */
  isWaitingForDocumentProcessing?: boolean;
  onAttachedDocumentClick?: (documentId: string) => void;
  onDraftClick?: (draft: DraftInfo) => void;
  onSavePromptToToolkit?: (prompt: string) => void;
  /** Copies the full prompt (including any collapsed portion) into the chat input and expands it. */
  onEditPrompt?: (prompt: string) => void;
  /** Called when the user picks a Next-Steps / Level-up suggestion, so the parent can expand the input. */
  onExpandChatInput?: () => void;
  /**
   * Marks the chat's last assistant message: shows the response stamp, expands Next Steps, and
   * always shows the action row.
   */
  isLastAssistantMessage?: boolean;

  canEditChat: boolean;
  /** documentId -> documentName lookup used by citation tooltips. */
  documentNameById?: Map<string, string>;
}

interface UuidWidgetProps {
  uuid: string;
  citations: Record<string, Citation>;
  onCitationClick: (citation: Citation) => void;
  number: number;
  documentNameById?: Map<string, string>;
}

const UuidWidget = ({ uuid, citations = {}, onCitationClick, number, documentNameById }: UuidWidgetProps) => {
  const citation = citations[uuid];
  const documentName = citation?.documentId ? documentNameById?.get(citation.documentId) : undefined;

  return (
    <CitationButton
      onClick={() => onCitationClick(citation)}
      number={number}
      citationText={citation?.extract}
      documentName={documentName}
      pageNumber={citation?.pageNumber ?? undefined}
      isAiImageDescription={isAiGeneratedCitation(citation)}
    />
  );
};

/** Adds a space between back-to-back pills (`{{a}}{{b}}`) so they don't look like one. */
const spaceAdjacentCitationPills = (parts: ReactNode[]): ReactNode[] => {
  const spaced: ReactNode[] = [];
  let previousWasPill = false;

  parts.forEach((part) => {
    // `reactStringReplace` leaves an empty string between two consecutive matches. It renders
    // nothing, so it must not count as text holding the pills apart.
    if (part === '') return;

    const isPill = isValidElement(part) && (part.type === UuidWidget || part.type === CitationPlaceholderPill);
    if (isPill && previousWasPill) spaced.push(' ');

    spaced.push(part);
    previousWasPill = isPill;
  });

  return spaced;
};

/**
 * Copying a selection drops citations, like the copy button. `copy` fires on the message where the
 * selection starts, so it handles the whole selection.
 */
const copySelectionWithoutPills = (event: ClipboardEvent<HTMLDivElement>) => {
  copySelectionWithoutCitations(event, CHAT_COPY_EXCLUDE_SELECTOR);
};

interface MessageBodyProps {
  content: string;
  markdownComponents: Components;
}

const MessageBody = ({ content, markdownComponents }: MessageBodyProps) => {
  const { body, showCursor } = splitStreamingCursorContent(content || '');

  return (
    <span
      className={classNames({
        'streaming-message-with-cursor': showCursor,
      })}
    >
      <span className="streaming-message-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={markdownComponents}>
          {body}
        </ReactMarkdown>
      </span>
      {showCursor && (
        <span className="streaming-cursor text-theme-black ml-1" aria-hidden>
          {STREAMING_CURSOR_CHAR}
        </span>
      )}
    </span>
  );
};

interface CopyButtonProps {
  onClick: () => void;
  /** When true, the copy icon is swapped for a tick — signalling recent success. */
  showCheck?: boolean;
}

const COPY_TOOLTIP = 'Copy to clipboard';
export const SAVE_PROMPT_TO_TOOLKIT_TOOLTIP = 'Save prompt to toolkit';
const EDIT_PROMPT_TOOLTIP = 'Edit prompt';
const NEXT_STEPS_SHOW_TOOLTIP = 'Show next steps';
const NEXT_STEPS_HIDE_TOOLTIP = 'Hide next steps';
const FEEDBACK_REMINDER_TEXT = 'How did Fletch do on this response?';
const FAILED_PROMPT_TEXT = 'Fletch failed to generate a response to this message';

/** How long the tick stays showing after a copy before the icon returns to the clipboard glyph. */
const COPY_CHECK_DURATION_MS = 3000;

const CopyButton = ({ onClick, showCheck = false }: CopyButtonProps) => {
  const targetRef = useRef<HTMLButtonElement>(null);
  const tooltipContent = showCheck ? 'Copied to clipboard' : COPY_TOOLTIP;

  return (
    <>
      <button
        ref={targetRef}
        type="button"
        onClick={onClick}
        aria-label={tooltipContent}
        className="text-theme-text-grey hover:text-theme-black flex h-6 w-6 cursor-pointer items-center justify-center border-none bg-transparent p-0 transition-colors"
      >
        {showCheck ? <CheckIconV2 width={14} height={12} /> : <CopyIcon className="overflow-visible" />}
      </button>
      <Tooltip
        target={targetRef as RefObject<HTMLElement>}
        content={tooltipContent}
        position="bottom"
        className={TOOLTIP_SURFACE_BOTTOM}
      />
    </>
  );
};

interface EditPromptButtonProps {
  onClick: () => void;
}

const EditPromptButton = ({ onClick }: EditPromptButtonProps) => {
  const targetRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <button
        ref={targetRef}
        type="button"
        onClick={onClick}
        aria-label={EDIT_PROMPT_TOOLTIP}
        {...{ [KEEPS_CHAT_INPUT_OPEN_ATTR]: '' }}
        className="text-theme-text-grey hover:text-theme-black flex h-6 w-6 cursor-pointer items-center justify-center border-none bg-transparent p-0 transition-colors"
      >
        <PencilIcon width={14} height={14} />
      </button>
      <Tooltip
        target={targetRef as RefObject<HTMLElement>}
        content={EDIT_PROMPT_TOOLTIP}
        position="bottom"
        className={TOOLTIP_SURFACE_BOTTOM}
      />
    </>
  );
};

interface NextStepsToggleButtonProps {
  isOpen: boolean;
  onClick: () => void;
}

const NextStepsToggleButton = ({ isOpen, onClick }: NextStepsToggleButtonProps) => {
  const targetRef = useRef<HTMLButtonElement>(null);
  const label = isOpen ? NEXT_STEPS_HIDE_TOOLTIP : NEXT_STEPS_SHOW_TOOLTIP;

  return (
    <>
      <button
        ref={targetRef}
        type="button"
        onClick={onClick}
        aria-label={label}
        aria-expanded={isOpen}
        className="text-theme-text-grey hover:text-theme-black flex h-6 w-6 cursor-pointer items-center justify-center border-none bg-transparent p-0 transition-colors"
      >
        <NextStepsIcon
          className={classNames('transition-transform', isOpen ? 'rotate-90' : 'rotate-0')}
          width={16}
          height={14}
        />
      </button>
      <Tooltip
        target={targetRef as RefObject<HTMLElement>}
        content={label}
        position="bottom"
        className={TOOLTIP_SURFACE_BOTTOM}
      />
    </>
  );
};

interface UserBubbleShapeProps {
  width: number;
  height: number;
  isFailed: boolean;
}

const UserBubbleShape = ({ width, height, isFailed }: UserBubbleShapeProps) => {
  const w = width;
  const h = height;
  const path = `M 24 0.5 L ${w - 24} 0.5 Q ${w - 0.5} 0.5 ${w - 0.5} 24 L ${w - 0.5} ${h - 16} Q ${w - 0.5} ${h + 8} ${w + 10} ${h + 8} Q ${w - 6} ${h + 4} ${w - 12} ${h - 0.5} L 24 ${h - 0.5} Q 0.5 ${h - 0.5} 0.5 ${h - 24} L 0.5 24 Q 0.5 0.5 24 0.5 Z`;

  return (
    <svg
      aria-hidden
      className="pointer-events-none absolute top-0 left-0 drop-shadow-[0_1px_3px_rgba(0,0,0,0.07)]"
      width={w + 14}
      height={h + 12}
      viewBox={`0 0 ${w + 14} ${h + 12}`}
    >
      <path
        d={path}
        fill={isFailed ? 'var(--theme-background-red)' : 'var(--theme-white)'}
        fillOpacity="0.9"
        stroke={isFailed ? 'var(--theme-background-red)' : 'var(--theme-border-grey)'}
        strokeWidth="1"
        strokeLinejoin="round"
      />
    </svg>
  );
};

interface SavePromptToToolkitButtonProps {
  onClick: () => void;
}

const SavePromptToToolkitButton = ({ onClick }: SavePromptToToolkitButtonProps) => {
  const targetRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <button
        ref={targetRef}
        type="button"
        onClick={onClick}
        aria-label={SAVE_PROMPT_TO_TOOLKIT_TOOLTIP}
        className="text-theme-text-grey hover:text-theme-black flex h-6 w-6 cursor-pointer items-center justify-center border-none bg-transparent p-0 transition-colors"
      >
        <StarOutlinedIcon className="overflow-visible" />
      </button>
      <Tooltip
        target={targetRef as RefObject<HTMLElement>}
        content={SAVE_PROMPT_TO_TOOLKIT_TOOLTIP}
        position="bottom"
        className={TOOLTIP_SURFACE_BOTTOM}
      />
    </>
  );
};

export const ChatMessage = ({
  message,
  onCitationClick,
  searchQuery,
  globalOrderedUuids,
  chatCitations,
  documentsByMessageId,
  promptAttachmentStatusById,
  isWaitingForDocumentProcessing,
  onAttachedDocumentClick,
  onDraftClick,
  onSavePromptToToolkit,
  onEditPrompt,
  onExpandChatInput,
  isLastAssistantMessage = false,
  canEditChat,
  documentNameById: documentNameByIdProp,
}: ChatMessageProps) => {
  const [expandedTableChildren, setExpandedTableChildren] = useState<ReactNode | null>(null);
  const [isTableDialogVisible, setIsTableDialogVisible] = useState(false);
  const [isUserMessageExpanded, setIsUserMessageExpanded] = useState(false);
  const [isNextStepsOpen, setIsNextStepsOpen] = useState(isLastAssistantMessage);
  const [isCopied, setIsCopied] = useState(false);
  const copyResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [postEventMessageCopied] = usePostEventMessageCopiedMutation();
  const projectChat = useProjectChatContext();

  // Collapse when a newer response arrives; open when this becomes the latest.
  useEffect(() => {
    setIsNextStepsOpen(isLastAssistantMessage);
  }, [isLastAssistantMessage]);

  useEffect(() => {
    return () => {
      if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current);
    };
  }, []);

  const hasNextSteps = canEditChat && (Boolean(message.suggestions?.length) || Boolean(message.levelUp?.length));

  // Use the parent's lookup if passed; otherwise build it from the per-message attachments.
  const documentNameById = useMemo(() => {
    if (documentNameByIdProp) return documentNameByIdProp;
    const map = new Map<string, string>();
    documentsByMessageId?.forEach((docs) => {
      docs.forEach((doc) => {
        if (doc.documentId && doc.documentName) map.set(doc.documentId, doc.documentName);
      });
    });
    return map;
  }, [documentNameByIdProp, documentsByMessageId]);

  const isUser = message.role === MessageRole.User;
  const isFailed = isUser && message.status === 'FAILED';
  const fromApi =
    message.messageId && documentsByMessageId?.size ? documentsByMessageId.get(message.messageId) : undefined;
  const optimistic = message.attachedDocuments;
  const promptAttachments: MessageAttachedDocument[] = (() => {
    if (optimistic && optimistic.length > 0) {
      const apiById = new Map((fromApi ?? []).map((d) => [d.documentId, d]));
      return optimistic.map((opt) => {
        const api = apiById.get(opt.documentId);
        return api ? { ...opt, ...api, documentName: api.documentName ?? opt.documentName } : opt;
      });
    }
    if (fromApi && fromApi.length > 0) {
      return fromApi;
    }
    return [];
  })();
  // Hide the copy button until the full text has arrived.
  const isResponseStreamingIn = Boolean(message.isOptimistic) && !message.finalResultReceived;
  const showCopy = Boolean(message.content && message.content.trim().length > 0) && !isResponseStreamingIn;
  const canSubmitFeedback = Boolean(message.messageId && !message.isOptimistic);
  const { phase: feedbackReminderPhase, dismiss: dismissFeedbackReminder } = useFeedbackReminder(
    message.messageId,
    isLastAssistantMessage && canSubmitFeedback && Boolean(message.askForFeedback),
  );
  const hasGfmTable = useMemo(() => containsGfmTable(message.content ?? ''), [message.content]);

  const USER_MESSAGE_COLLAPSE_LIMIT = 400;

  const userContent = message.content ?? '';
  const canCollapseUserMessage = isUser && userContent.length > USER_MESSAGE_COLLAPSE_LIMIT;
  // Expand when a search match is hidden behind "See more". Only for long user messages with an active query.
  const shouldExpandForSearch = useMemo(() => {
    if (!canCollapseUserMessage) return false;
    const q = searchQuery?.trim().toLowerCase();
    if (!q) return false;
    return userContent.slice(USER_MESSAGE_COLLAPSE_LIMIT).toLowerCase().includes(q);
  }, [canCollapseUserMessage, searchQuery, userContent]);
  const isUserMessageCollapsed = canCollapseUserMessage && !isUserMessageExpanded && !shouldExpandForSearch;
  const collapsedUserContent = isUserMessageCollapsed ? userContent.slice(0, USER_MESSAGE_COLLAPSE_LIMIT) : userContent;

  const expandUserMessage = useCallback(() => setIsUserMessageExpanded(true), []);
  const collapseUserMessage = useCallback(() => setIsUserMessageExpanded(false), []);

  const userMessageBodyRef = useRef<HTMLDivElement>(null);
  const assistantMessageBodyRef = useRef<HTMLDivElement>(null);

  // Measured so the SVG bubble shape can match the message size.
  const userBubbleRef = useRef<HTMLDivElement>(null);
  const [userBubbleSize, setUserBubbleSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    if (!isUser || !userBubbleRef.current) return;
    const el = userBubbleRef.current;
    const measure = () => setUserBubbleSize({ w: el.offsetWidth, h: el.offsetHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isUser]);

  const handleCopy = async () => {
    if (!message.content) return;

    const messageBodyRef = isUser ? userMessageBodyRef : assistantMessageBodyRef;
    let copiedContent = cleanChatMessageForCopy(message.content);
    if (messageBodyRef.current) {
      const clone = messageBodyRef.current.cloneNode(true) as HTMLElement;
      clone.querySelectorAll(CHAT_COPY_EXCLUDE_SELECTOR).forEach((el) => el.remove());
      clone.style.cssText = 'position:absolute;left:-9999px;top:-9999px;opacity:0;pointer-events:none';
      document.body.appendChild(clone);
      copiedContent = cleanChatMessageForCopy(clone.innerText) || copiedContent;
      document.body.removeChild(clone);
    }

    await copyToClipboard(copiedContent);

    setIsCopied(true);
    if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current);
    copyResetTimerRef.current = setTimeout(() => setIsCopied(false), COPY_CHECK_DURATION_MS);

    if (message.messageId && !message.isOptimistic) {
      postEventMessageCopied({ messageId: message.messageId });
    }
  };

  const wrapWithHighlight = useCallback(
    (node: ReactNode) => (searchQuery?.trim() ? highlightMatches(node, searchQuery) : node),
    [searchQuery],
  );

  const orderedUuids = useMemo(() => {
    if (globalOrderedUuids?.length) return globalOrderedUuids;
    if (!message.content) return [];
    const matches = Array.from(message.content.matchAll(new RegExp(UUID_REGEX.source, UUID_REGEX.flags)));
    const uuids = matches.map((m) => m[1]);
    return Array.from(new Set(uuids));
  }, [message.content, globalOrderedUuids]);

  // Keep the markdown components map stable — a new map remounts the whole message and loses the
  // scroll position. `onCitationClick` changes on every download, so pin it.
  const handleCitationClick = useEventCallback(onCitationClick);

  const replaceUuidsInChildren = useCallback(
    (children: ReactNode) => {
      const parts = reactStringReplace(children as (string | ReactNode)[], UUID_REGEX, (match, i) => {
        const index = orderedUuids.indexOf(match);
        if (index === -1 || !chatCitations?.[match]) {
          // While streaming, show a same-size placeholder pill so the text doesn't reflow when the number arrives.
          if (isResponseStreamingIn) return <CitationPlaceholderPill key={match + i} />;
          // The citation map can load after the message. Show an empty pill for anything that looks
          // like a citation id, never the raw uuid.
          if (isUuidLike(match)) return <CitationPlaceholderPill key={match + i} isLoading={false} />;
          // Not a citation id at all - render the literal `{{match}}` so we don't end up with a
          // pill pointing at nothing for unrelated `{{...}}` strings.
          return `{{${match}}}`;
        }
        return (
          <UuidWidget
            key={match + i}
            uuid={match}
            number={index + 1}
            onCitationClick={handleCitationClick}
            citations={chatCitations}
            documentNameById={documentNameById}
          />
        );
      });

      return spaceAdjacentCitationPills(parts);
    },
    [chatCitations, handleCitationClick, orderedUuids, documentNameById, isResponseStreamingIn],
  );

  const openTableDialog = useCallback((children: ReactNode) => {
    setExpandedTableChildren(children);
    setIsTableDialogVisible(true);
  }, []);

  const closeTableDialog = useCallback(() => {
    setIsTableDialogVisible(false);
    setExpandedTableChildren(null);
  }, []);

  const markdownComponents: Components = useMemo(
    () => ({
      h1: ({ children }) => (
        <h1 className="text-theme-black mt-6 mb-4 text-xl font-bold first:mt-0">{wrapWithHighlight(children)}</h1>
      ),
      h2: ({ children }) => (
        <h2 className="text-theme-black mt-5 mb-3 text-lg font-bold first:mt-0">{wrapWithHighlight(children)}</h2>
      ),
      h3: ({ children }) => (
        <h3 className="text-theme-black mt-4 mb-2 text-base font-bold first:mt-0">{wrapWithHighlight(children)}</h3>
      ),
      h4: ({ children }) => (
        <h4 className="text-theme-black mt-3 mb-2 text-sm font-bold first:mt-0">{wrapWithHighlight(children)}</h4>
      ),
      ul: ({ children }) => (
        <ul className="text-theme-black mb-3 list-disc pl-5 leading-6">{wrapWithHighlight(children)}</ul>
      ),

      ol: ({ children, start }) => (
        <ol start={start} className="text-theme-black mb-3 list-decimal pl-5 leading-6">
          {wrapWithHighlight(children)}
        </ol>
      ),
      li: ({ children }) => {
        const processedChildren = replaceUuidsInChildren(children);
        return <li className="text-theme-black mb-1">{wrapWithHighlight(processedChildren)}</li>;
      },
      a: ({ href, children }) => (
        <a
          href={href}
          className="font-medium text-blue-600 transition-colors hover:text-blue-800 hover:underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          {wrapWithHighlight(children)}
        </a>
      ),
      p: ({ children }) => {
        const processedChildren = replaceUuidsInChildren(children);
        return (
          <p className="text-theme-black mb-3 leading-6 break-words last:mb-0">
            {wrapWithHighlight(processedChildren)}
          </p>
        );
      },
      code: ({ inline, className, children, ...props }: ComponentPropsWithoutRef<'code'> & { inline?: boolean }) => {
        return !inline ? (
          <pre className="mb-3 block overflow-x-auto rounded-md border border-gray-200 bg-gray-100 p-3 text-sm">
            <code className={className} {...props}>
              {children}
            </code>
          </pre>
        ) : (
          <code
            className="text-fletchers-red-10 mx-0.5 rounded border border-gray-200 bg-gray-100 px-1.5 py-0.5"
            {...props}
          >
            {children}
          </code>
        );
      },
      blockquote: ({ children }) => (
        <blockquote className="my-3 rounded-r-sm border-l-4 border-gray-300 bg-gray-50 py-1 pl-4 text-gray-600 italic">
          {children}
        </blockquote>
      ),
      table: ({ children }) => <MarkdownTable onOpenFullView={openTableDialog}>{children}</MarkdownTable>,
      thead: ({ children }) => <thead className="bg-theme-background-grey">{children}</thead>,
      tbody: ({ children }) => (
        <tbody className="divide-theme-background-grey bg-theme-white divide-y">{children}</tbody>
      ),
      tr: ({ children }) => <tr>{children}</tr>,
      th: ({ children }) => {
        const processedChildren = replaceUuidsInChildren(children);
        return (
          <th className="bg-theme-background-grey text-theme-text-grey sticky top-0 z-10 min-w-37.5 px-3 py-2 text-left text-sm font-semibold tracking-wider">
            {wrapWithHighlight(processedChildren)}
          </th>
        );
      },
      td: ({ children }) => {
        const processedChildren = replaceUuidsInChildren(children);
        return (
          <ExpandableTableCell className="text-theme-text-grey min-w-37.5 px-3 py-2 align-top text-sm break-words whitespace-normal">
            {wrapWithHighlight(processedChildren)}
          </ExpandableTableCell>
        );
      },
      hr: () => <hr className="my-4 border-gray-200" />,
      img: ({ src, alt }) => (
        <img src={src} alt={alt} className="my-3 h-auto max-w-full rounded-lg border border-gray-200 shadow-sm" />
      ),
    }),
    [openTableDialog, replaceUuidsInChildren, wrapWithHighlight],
  );

  const tableDialog =
    expandedTableChildren && message.content ? (
      <ChatTableDialog
        visible={isTableDialogVisible}
        onHide={closeTableDialog}
        title="Full view"
        tableChildren={expandedTableChildren}
      />
    ) : null;

  if (isUser) {
    return (
      <div className="group my-4 flex w-full min-w-0 justify-end" onCopy={copySelectionWithoutPills}>
        <div className={`flex w-full min-w-0 flex-col items-end gap-2 ${hasGfmTable ? 'max-w-none' : 'max-w-150'}`}>
          {promptAttachments.length > 0 && (
            <div className="flex w-full max-w-150 flex-wrap justify-end gap-2">
              {promptAttachments.map((doc) => (
                <UploadFileButton
                  key={doc.documentId}
                  itemClassName="whitespace-nowrap !my-0"
                  documentId={doc.documentId}
                  documentName={doc.documentName}
                  status={isWaitingForDocumentProcessing ? promptAttachmentStatusById?.get(doc.documentId) : undefined}
                  onClick={() => onAttachedDocumentClick?.(doc.documentId)}
                />
              ))}
            </div>
          )}
          <div
            ref={userBubbleRef}
            className="relative w-fit max-w-full min-w-0 px-[18px] py-4 text-left text-sm font-normal"
          >
            {userBubbleSize.w > 0 && (
              <UserBubbleShape width={userBubbleSize.w} height={userBubbleSize.h} isFailed={isFailed} />
            )}
            <div className={classNames('relative', { 'failed-prompt-text': isFailed })}>
              {isUserMessageCollapsed ? (
                <div className="text-theme-black break-words whitespace-pre-wrap">
                  {collapsedUserContent}
                  {'…'}
                  <button
                    type="button"
                    className="text-theme-dark-green ml-1 inline cursor-pointer font-semibold hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      expandUserMessage();
                    }}
                  >
                    See more
                  </button>
                </div>
              ) : (
                <>
                  <div ref={userMessageBodyRef}>
                    <MessageBody content={message.content} markdownComponents={markdownComponents} />
                  </div>
                  {canCollapseUserMessage && (
                    <button
                      type="button"
                      className="text-theme-dark-green mt-1 inline cursor-pointer font-semibold hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        collapseUserMessage();
                      }}
                    >
                      See less
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          {isFailed && (
            <p className="text-theme-text-red text-right text-[0.6875rem] leading-4">{FAILED_PROMPT_TEXT}</p>
          )}
          {tableDialog}

          <div className="flex items-center gap-2 overflow-visible opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 focus-within:opacity-100">
            {showCopy && <CopyButton onClick={handleCopy} showCheck={isCopied} />}
            {onEditPrompt && <EditPromptButton onClick={() => onEditPrompt(userContent)} />}
            {onSavePromptToToolkit && <SavePromptToToolkitButton onClick={() => onSavePromptToToolkit(userContent)} />}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group my-4 flex w-full min-w-0 flex-col" onCopy={copySelectionWithoutPills}>
      <div
        className={`text-theme-black flex w-full min-w-0 flex-col text-sm font-normal ${hasGfmTable ? 'max-w-none' : 'max-w-200'}`}
      >
        <div ref={assistantMessageBodyRef}>
          <MessageBody content={message.content} markdownComponents={markdownComponents} />
        </div>
        {tableDialog}
        {message.drafts && message.drafts.length > 0 && (
          <div className="mt-3 flex flex-col gap-2">
            {message.drafts.map((draft) => {
              const filename = draft.title.toLowerCase().endsWith('.docx') ? draft.title : `${draft.title}.docx`;
              if (projectChat) {
                return (
                  <DraftArtefactCard
                    key={draft.draftVersionId}
                    fileName={filename}
                    saveState={projectChat.getDraftSaveState(draft.draftVersionId)}
                    onOpen={() => onDraftClick?.(draft)}
                    onSave={() => projectChat.saveDraftToProject(draft)}
                  />
                );
              }
              return (
                <button
                  key={draft.draftVersionId}
                  type="button"
                  onClick={() => onDraftClick?.(draft)}
                  style={{ borderColor: 'rgba(74, 126, 231, 0.4)' }}
                  className="group/draft-chip text-theme-black bg-theme-white hover:bg-theme-draft-chip-hover relative flex w-fit max-w-75 cursor-pointer items-center gap-3 rounded-md border px-4 py-4 transition-colors"
                >
                  <DocxIcon width={30} height={20} className="shrink-0" />
                  <span className="min-w-0 flex-1 truncate text-left text-sm font-semibold transition-[margin] duration-150 group-hover/draft-chip:mr-14">
                    {filename}
                  </span>
                  <span
                    style={{ color: 'var(--theme-draft-chip-accent)' }}
                    className="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-sm font-semibold opacity-0 transition-opacity group-hover/draft-chip:opacity-100"
                  >
                    Open
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {(showCopy || canSubmitFeedback || hasNextSteps) && message.content && (
          // The newest response always shows its actions; older ones on hover or focus.
          <div
            className={classNames(
              'mt-2 flex items-end gap-2 transition-opacity',
              isLastAssistantMessage
                ? 'opacity-100'
                : 'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 focus-within:opacity-100',
            )}
          >
            {showCopy && <CopyButton onClick={handleCopy} showCheck={isCopied} />}
            {canSubmitFeedback && (
              <MessageFeedbackButtons
                messageId={message.messageId}
                reminderPhase={feedbackReminderPhase}
                onFeedbackClick={dismissFeedbackReminder}
              />
            )}
            {hasNextSteps && (
              <NextStepsToggleButton isOpen={isNextStepsOpen} onClick={() => setIsNextStepsOpen((prev) => !prev)} />
            )}
          </div>
        )}
        {feedbackReminderPhase !== 'off' && (
          <div
            className={classNames('feedback-reminder-banner', { 'is-leaving': feedbackReminderPhase === 'leaving' })}
          >
            <div className="min-h-0 overflow-hidden">
              <p className="pt-2">{FEEDBACK_REMINDER_TEXT}</p>
            </div>
          </div>
        )}
      </div>
      {/* Outside the message column so it aligns with the full chat width. Waits for hydration, so
         `GET /chat` has refetched and the time belongs to this response. */}
      {isLastAssistantMessage && canSubmitFeedback && <ChatResponseStamp />}
      {hasNextSteps && isNextStepsOpen && (
        <NextSteps
          suggestions={message.suggestions ?? []}
          levelUp={message.levelUp}
          onSuggestionSelected={onExpandChatInput}
        />
      )}
    </div>
  );
};
